import { useState, useEffect } from 'react';
import api from '../../../api/client';

const EMPTY_FORM = { name: '', role: '', rateCents: '', rateUnit: 'hour', startDate: '' };

export default function WorkersPage() {
  const [workers,  setWorkers]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);
  const [addOpen,  setAddOpen]  = useState(false);
  const [form,     setForm]     = useState(EMPTY_FORM);
  const [saving,   setSaving]   = useState(false);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get('/vms/workers');
      setWorkers(data.data?.items || []);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  function patch(key, val) {
    setForm((f) => ({ ...f, [key]: val }));
  }

  async function handleAdd(e) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await api.post('/vms/workers', {
        name:      form.name,
        role:      form.role,
        rateCents: Number(form.rateCents),
        rateUnit:  form.rateUnit,
        startDate: form.startDate,
      });
      setForm(EMPTY_FORM);
      setAddOpen(false);
      await load();
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div data-testid="vms-workers-page" data-loading={loading ? 'true' : 'false'}>
      <h2>Workers</h2>

      {error && <p style={{ color: 'red' }}>{error}</p>}

      <div style={{ marginBottom: '1rem' }}>
        <button data-testid="vms-worker-add" onClick={() => setAddOpen(true)}>
          Add Worker
        </button>
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <table style={{ width: '100%' }}>
          <thead>
            <tr>
              <th>Name</th>
              <th>Role</th>
              <th>Rate</th>
              <th>Start Date</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {workers.map((w) => (
              <tr key={w.id} data-testid={`vms-worker-row-${w.id}`}>
                <td>{w.name}</td>
                <td>{w.role}</td>
                <td>{w.rate_cents != null ? `${(w.rate_cents / 100).toFixed(2)} / ${w.rate_unit}` : '—'}</td>
                <td>{w.start_date?.slice(0, 10)}</td>
                <td>{w.status}</td>
              </tr>
            ))}
            {!workers.length && <tr><td colSpan={5}>No workers yet.</td></tr>}
          </tbody>
        </table>
      )}

      {addOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 8, padding: '1.5rem', minWidth: 360 }}>
            <h3>Add Worker</h3>
            <form onSubmit={handleAdd}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <input
                  data-testid="vms-worker-name-input"
                  placeholder="Name"
                  value={form.name}
                  onChange={(e) => patch('name', e.target.value)}
                  required
                />
                <input
                  data-testid="vms-worker-role-input"
                  placeholder="Role"
                  value={form.role}
                  onChange={(e) => patch('role', e.target.value)}
                  required
                />
                <input
                  data-testid="vms-worker-rate-input"
                  placeholder="Rate (cents)"
                  type="number"
                  min={0}
                  value={form.rateCents}
                  onChange={(e) => patch('rateCents', e.target.value)}
                  required
                />
                <select value={form.rateUnit} onChange={(e) => patch('rateUnit', e.target.value)}>
                  <option value="hour">per hour</option>
                  <option value="day">per day</option>
                  <option value="week">per week</option>
                  <option value="month">per month</option>
                </select>
                <input
                  data-testid="vms-worker-start-input"
                  type="date"
                  value={form.startDate}
                  onChange={(e) => patch('startDate', e.target.value)}
                  required
                />
              </div>
              {error && <p style={{ color: 'red', marginTop: '0.5rem' }}>{error}</p>}
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
                <button type="submit" data-testid="vms-worker-form-submit" disabled={saving}>
                  {saving ? 'Saving...' : 'Save'}
                </button>
                <button type="button" onClick={() => { setAddOpen(false); setError(null); }}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
